<script setup>
import { computed, defineModel, ref } from 'vue'
import {
	Listbox,
	ListboxLabel,
	ListboxButton,
	ListboxOptions,
	ListboxOption
} from '@headlessui/vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
	id: { type: String, required: true },
	items: { type: Array, default: () => [] },
	title: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	showOptionsOnTop: { type: Boolean, default: false },
	inputHeight: { type: String, default: '' },
	labelTextSize: { type: String, default: '' },
	labelClass: { type: String, default: '' },//AJL
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	readonly: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	icon: { type: String, default: 'none' },
	maxLength: { type: Number, default: 255 },
	maxSearchResultsHeight: { type: String, default: 'max-h-40' },
	limit: { type: Number, default: 10 }, //Limit number of results
})

const selectedItems = defineModel({
	type: Array,
	default: () => []
})

const comboboxRef = ref(null)
const isLoading = ref(false)

const labelCustomClass = computed(() =>
{
	return [props.labelClass, props.labelTextSize].join(' ')
})

const placeholderValues = computed(() => {

	const selected = selectedItems.value.map(p_item => p_item.text)

	return selected.length === 0 ? props.placeholder : selected.join(", ")

});

/**
 *
 * @param p_person
 * @returns {boolean}
 */
function isSelected(p_person)
{
	return selectedItems.value.findIndex((p_item) => p_item.id === p_person.id) >= 0;
}

</script>

<template>
	<Listbox as="div" v-model="selectedItems" multiple nullable class="mb-4" v-slot="{ open }">
		<div ref="comboboxRef">
			<ListboxLabel v-if="props.title" class="leading-6 mb-3 block text-label font-normal" :class="labelCustomClass">{{ props.title }}</ListboxLabel>
		</div>
		<div class="relative">

			<ListboxButton
				class="block w-full font-medium rounded bg-neutral py-3 pl-4 pr-2 text-label outline-none border border-neutral placeholder:text-placeholder"
				:class="{
				'focus:ring-1 focus:ring-primary-bold-hover focus:border-primary-bold-hover group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover': !props.disabled
				}">


				<div class="flex flex-row justify-between runc">
					<div class="truncate whitespace-pre" :class="[ props.disabled ? 'text-disabled' : 'text-placeholder']">&nbsp;{{ open ? props.placeholder : placeholderValues }}</div>
					<div>
						<ChevronUpIcon v-if="open" class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
						<ChevronDownIcon v-else class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
					</div>
				</div>
			</ListboxButton>

			<ListboxOptions class="absolute z-10 mt-1 w-full overflow-auto rounded bg-neutral mb-1 text-base font-medium border border-neutral focus:outline-none sm:text-sm"
								  :class="[
									  { 'top-full': !showOptionsOnTop,
									   'ring-1 ring-primary-bold-hover border-primary-bold-hover': !props.disabled
									   },
									  maxSearchResultsHeight]" >
				<div v-if="!isLoading">
					<ListboxOption  v-for="item in items" :key="item.id" :value="item" :disabled="props.disabled">
						<li :class="[
							'relative cursor-default select-none py-2 pl-3 pr-4 text-placeholder',
							isSelected(item) ? 'bg-primary-subtle outline-none' : 'text-label',]">
							<div class="flex flex-row justify-between">
								<div :class="['block truncate whitespace-pre-wrap', isSelected(item) ? 'text-label' : '']">
									{{ item.text }}
								</div>
								<div class="w-6">
									<CheckIcon v-show="isSelected(item)" class="h-4 w-4 mt-1 ml-1 text-icon-neutral" aria-hidden="true" />
								</div>
							</div>
						</li>
					</ListboxOption>
				</div>

			</ListboxOptions>

		</div>

	</Listbox>
</template>

<style scoped>

</style>